import React, { useState, useEffect } from 'react';
import { Settings, User, Bell, Shield, Database, Palette, Globe, Save, RefreshCw, CreditCard, Printer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { alertService } from '../services/alertService';
import printService from '../services/printService';

interface SettingsData {
  // Configurações Gerais
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  timezone: string;
  language: string;
  currency: string;
  
  // Configurações de Notificação
  emailNotifications: boolean;
  pushNotifications: boolean;
  salesAlerts: boolean;
  lowStockAlerts: boolean;
  
  // Configurações de Segurança
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  
  // Configurações de Sistema
  autoBackup: boolean;
  backupFrequency: string;
  maintenanceMode: boolean;
  debugMode: boolean;
  
  // Configurações de Interface
  theme: string;
  sidebarCollapsed: boolean;
  showAnimations: boolean;
  compactMode: boolean;
  // Impressão
  printerMode: 'system' | 'manual';
  printerName: string;
}

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [qzAvailable, setQzAvailable] = useState<boolean>(false);
  const [printers, setPrinters] = useState<string[]>([]);
  const [defaultPrinter, setDefaultPrinter] = useState<string>('');
  const [qzHint, setQzHint] = useState<string>('');
  const [settings, setSettings] = useState<SettingsData>({
    // Configurações Gerais
    companyName: 'Comercial Admin',
    companyEmail: 'admin@comercial.com',
    companyPhone: '(11) 99999-9999',
    timezone: 'America/Sao_Paulo',
    language: 'pt-BR',
    currency: 'BRL',
    
    // Configurações de Notificação
    emailNotifications: true,
    pushNotifications: true,
    salesAlerts: true,
    lowStockAlerts: true,
    
    // Configurações de Segurança
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
    
    // Configurações de Sistema
    autoBackup: true,
    backupFrequency: 'daily',
    maintenanceMode: false,
    debugMode: false,
    
    // Configurações de Interface
    theme: 'light',
    sidebarCollapsed: false,
    showAnimations: true,
    compactMode: false,
    // Impressão
    printerMode: 'system',
    printerName: '',
  });

  const tabs = [
    { id: 'general', label: 'Geral', icon: Settings },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'system', label: 'Sistema', icon: Database },
    { id: 'interface', label: 'Interface', icon: Palette },
    { id: 'printing', label: 'Impressão', icon: Printer },
  ];

  const timezones = [
    { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
    { value: 'America/New_York', label: 'Nova York (UTC-5)' },
    { value: 'Europe/London', label: 'Londres (UTC+0)' },
    { value: 'Asia/Tokyo', label: 'Tóquio (UTC+9)' },
  ];

  const languages = [
    { value: 'pt-BR', label: 'Português (Brasil)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'es-ES', label: 'Español' },
  ];

  const currencies = [
    { value: 'BRL', label: 'Real (R$)' },
    { value: 'USD', label: 'Dólar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
  ];

  const handleInputChange = (field: keyof SettingsData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simular salvamento das configurações
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Salvar no localStorage para persistência
      localStorage.setItem('app-settings', JSON.stringify(settings));
      
      alertService.toast('success', 'Configurações salvas com sucesso!');
    } catch (error) {
      alertService.toast('error', 'Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Resetar para configurações padrão
    const defaultSettings: SettingsData = {
      companyName: 'Comercial Admin',
      companyEmail: 'admin@comercial.com',
      companyPhone: '(11) 99999-9999',
      timezone: 'America/Sao_Paulo',
      language: 'pt-BR',
      currency: 'BRL',
      emailNotifications: true,
      pushNotifications: true,
      salesAlerts: true,
      lowStockAlerts: true,
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordExpiry: 90,
      autoBackup: true,
      backupFrequency: 'daily',
      maintenanceMode: false,
      debugMode: false,
      theme: 'light',
      sidebarCollapsed: false,
      showAnimations: true,
      compactMode: false,
      // Impressão
      printerMode: 'system',
      printerName: '',
    };
    
    setSettings(defaultSettings);
    alertService.toast('info', 'Configurações resetadas para o padrão');
  };

  useEffect(() => {
    // Carregar configurações salvas
    const savedSettings = localStorage.getItem('app-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    }
    // Detectar QZ Tray e listar impressoras
    (async () => {
      try {
        const available = await printService.isQZAvailable();
        setQzAvailable(available);
        if (available) {
          const def = await printService.getDefaultPrinter();
          setDefaultPrinter(def || '');
          setQzHint('QZ Tray conectado. Impressão direta disponível.');
        } else {
          setQzHint('QZ Tray não detectado. Será usado o diálogo do navegador.');
        }
      } catch {
        setQzHint('Não foi possível detectar o QZ Tray.');
      }
    })();
  }, []);

  const refreshPrinters = async () => {
    try {
      const available = await printService.isQZAvailable();
      setQzAvailable(available);
      if (!available) { setPrinters([]); return; }
      const qz = (window as any).qz;
      const list = await qz?.printers?.list?.();
      if (Array.isArray(list)) setPrinters(list);
      const def = await printService.getDefaultPrinter();
      setDefaultPrinter(def || '');
      if (!settings.printerName && settings.printerMode === 'manual' && def) {
        setSettings(prev => ({ ...prev, printerName: def }));
      }
    } catch (e) {
      console.error('Falha ao atualizar lista de impressoras', e);
    }
  };

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome da Empresa
          </label>
          <input
            type="text"
            value={settings.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email da Empresa
          </label>
          <input
            type="email"
            value={settings.companyEmail}
            onChange={(e) => handleInputChange('companyEmail', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Telefone da Empresa
          </label>
          <input
            type="tel"
            value={settings.companyPhone}
            onChange={(e) => handleInputChange('companyPhone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fuso Horário
          </label>
          <select
            value={settings.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Idioma
          </label>
          <select
            value={settings.language}
            onChange={(e) => handleInputChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            {languages.map(lang => (
              <option key={lang.value} value={lang.value}>{lang.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Moeda
          </label>
          <select
            value={settings.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            {currencies.map(curr => (
              <option key={curr.value} value={curr.value}>{curr.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Notificações por Email</h3>
            <p className="text-sm text-gray-500">Receber notificações importantes por email</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.emailNotifications}
              onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Notificações Push</h3>
            <p className="text-sm text-gray-500">Receber notificações push no navegador</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.pushNotifications}
              onChange={(e) => handleInputChange('pushNotifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Alertas de Vendas</h3>
            <p className="text-sm text-gray-500">Notificar sobre novas vendas realizadas</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.salesAlerts}
              onChange={(e) => handleInputChange('salesAlerts', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Alertas de Estoque Baixo</h3>
            <p className="text-sm text-gray-500">Notificar quando produtos estiverem com estoque baixo</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.lowStockAlerts}
              onChange={(e) => handleInputChange('lowStockAlerts', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Autenticação de Dois Fatores</h3>
            <p className="text-sm text-gray-500">Adicionar uma camada extra de segurança</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.twoFactorAuth}
              onChange={(e) => handleInputChange('twoFactorAuth', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timeout da Sessão (minutos)
            </label>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
              min="5"
              max="480"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiração da Senha (dias)
            </label>
            <input
              type="number"
              value={settings.passwordExpiry}
              onChange={(e) => handleInputChange('passwordExpiry', parseInt(e.target.value))}
              min="30"
              max="365"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Backup Automático</h3>
            <p className="text-sm text-gray-500">Realizar backup automático dos dados</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.autoBackup}
              onChange={(e) => handleInputChange('autoBackup', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Frequência do Backup
          </label>
          <select
            value={settings.backupFrequency}
            onChange={(e) => handleInputChange('backupFrequency', e.target.value)}
            disabled={!settings.autoBackup}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="hourly">A cada hora</option>
            <option value="daily">Diário</option>
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Modo de Manutenção</h3>
            <p className="text-sm text-gray-500">Ativar modo de manutenção do sistema</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Modo Debug</h3>
            <p className="text-sm text-gray-500">Ativar logs detalhados para desenvolvimento</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.debugMode}
              onChange={(e) => handleInputChange('debugMode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderInterfaceTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tema
          </label>
          <select
            value={settings.theme}
            onChange={(e) => handleInputChange('theme', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="light">Claro</option>
            <option value="dark">Escuro</option>
            <option value="auto">Automático</option>
          </select>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Sidebar Recolhida</h3>
            <p className="text-sm text-gray-500">Manter a barra lateral recolhida por padrão</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sidebarCollapsed}
              onChange={(e) => handleInputChange('sidebarCollapsed', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Animações</h3>
            <p className="text-sm text-gray-500">Exibir animações na interface</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showAnimations}
              onChange={(e) => handleInputChange('showAnimations', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Modo Compacto</h3>
            <p className="text-sm text-gray-500">Usar interface mais compacta</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.compactMode}
              onChange={(e) => handleInputChange('compactMode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderPrintingTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          <p>
            {qzHint}
          </p>
          {!qzAvailable && (
            <p className="mt-1 text-xs text-gray-500">Instale o QZ Tray (qz.io) e deixe-o aberto para permitir impressão direta.</p>
          )}
        </div>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="printerMode"
              value="system"
              checked={settings.printerMode === 'system'}
              onChange={() => handleInputChange('printerMode', 'system')}
            />
            <span>Usar impressora padrão do sistema</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="printerMode"
              value="manual"
              checked={settings.printerMode === 'manual'}
              onChange={() => handleInputChange('printerMode', 'manual')}
            />
            <span>Selecionar manualmente</span>
          </label>
        </div>

        {settings.printerMode === 'manual' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome da impressora</label>
            {qzAvailable ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={settings.printerName || defaultPrinter || ''}
                    onChange={(e) => handleInputChange('printerName', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Selecione...</option>
                    {defaultPrinter && (
                      <option value={defaultPrinter}>Padrão: {defaultPrinter}</option>
                    )}
                    {printers.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <button onClick={refreshPrinters} className="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                    Atualizar lista
                  </button>
                </div>
                <p className="text-xs text-gray-500">Selecione a impressora térmica (ex.: POS‑80 / EPSON TM). O padrão é: {defaultPrinter || 'não detectado'}.</p>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Ex.: EPSON TM-T20II"
                  value={settings.printerName}
                  onChange={(e) => handleInputChange('printerName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Dica: copie o nome exatamente como aparece no Windows.</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} /> Salvar preferências
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 flex items-center gap-2"
          >
            <RefreshCw size={16} /> Resetar
          </button>
          <Link to="/pdv/cupom/15" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Testar impressão</Link>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'security':
        return renderSecurityTab();
      case 'system':
        return renderSystemTab();
      case 'interface':
        return renderInterfaceTab();
      case 'printing':
        return renderPrintingTab();
      default:
        return renderGeneralTab();
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Configurações</h1>
        <p className="text-gray-600">Gerencie as configurações do sistema</p>
      </div>

      {/* Layout com submenu vertical e conteúdo à direita */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Submenu vertical */}
        <aside className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <ul className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <li key={tab.id}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left ${
                        isActive ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`p-1 rounded-md ${isActive ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="font-medium text-sm">{tab.label}</span>
                    </button>
                  </li>
                );
              })}
              <li>
                <Link
                  to="/configuracoes/tipos-pagamento"
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left text-gray-700 hover:bg-gray-50"
                >
                  <span className="p-1 rounded-md bg-gray-100 text-gray-700">
                    <CreditCard className="h-4 w-4" />
                  </span>
                  <span className="font-medium text-sm">Tipos de Pagamento</span>
                </Link>
              </li>
            </ul>
          </div>
        </aside>

        {/* Conteúdo de Configurações */}
        <section className="md:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              {renderTabContent()}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Resetar</span>
              </button>
              
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{loading ? 'Salvando...' : 'Salvar Configurações'}</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;